"use client";

import { useState } from "react";
import Image from "next/image";
import { Plus } from "lucide-react";
import type { FaqCategoryKey, FaqItem } from "./content";
import { faqCategories } from "./content";

type Category = FaqCategoryKey | "wszystkie";

const categoryIcon = Object.fromEntries(
  faqCategories.map(({ key, icon }) => [key, icon]),
) as Record<FaqCategoryKey, (typeof faqCategories)[number]["icon"]>;

export function FaqSection({ items }: { items: FaqItem[] }) {
  const [activeCategory, setActiveCategory] = useState<Category>("wszystkie");
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);

  const visibleItems =
    activeCategory === "wszystkie" ? items : items.filter((item) => item.category === activeCategory);

  function selectCategory(category: Category) {
    setActiveCategory(category);
    const nextItems = category === "wszystkie" ? items : items.filter((item) => item.category === category);
    setOpenId(nextItems[0]?.id ?? null);
  }

  return (
    <section className="relative overflow-hidden bg-[#fdf0f4] py-14 lg:py-20">
      <Image
        src="/images/leaf-narrow-1.png"
        alt=""
        width={70}
        height={108}
        aria-hidden
        className="pointer-events-none absolute -right-2 top-8 w-16 rotate-12 opacity-20 lg:w-20"
      />

      <div className="relative mx-auto max-w-screen-lg px-6">
        <div className="mb-10 text-center">
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#b8305a]">
            Masz pytania?
          </p>
          <h2 className="font-[family-name:var(--font-playfair)] text-3xl leading-snug text-zinc-900 lg:text-4xl">
            Najczęściej zadawane{" "}
            <span
              className="font-script text-[#e0507a]"
              style={{ fontSize: "clamp(2rem, 3.5vw, 2.8rem)" }}
            >
              pytania <span className="text-[#e0507a]/60">♡</span>
            </span>
          </h2>
        </div>

        <nav
          className="mb-8 flex flex-wrap justify-center gap-2"
          aria-label="Kategorie pytań"
        >
          <button
            type="button"
            onClick={() => selectCategory("wszystkie")}
            aria-pressed={activeCategory === "wszystkie"}
            className={`rounded-full border px-4 py-2 text-sm font-bold transition-colors ${
              activeCategory === "wszystkie"
                ? "border-[#e0507a] bg-[#e0507a] text-white shadow-sm"
                : "border-[#e0507a]/25 bg-white text-zinc-600 hover:border-[#e0507a]/60 hover:text-[#e0507a]"
            }`}
          >
            Wszystkie
          </button>
          {faqCategories.map(({ key, label, icon: Icon }) => {
            const isActive = activeCategory === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => selectCategory(key)}
                aria-pressed={isActive}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition-colors ${
                  isActive
                    ? "border-[#e0507a] bg-[#e0507a] text-white shadow-sm"
                    : "border-[#e0507a]/25 bg-white text-zinc-600 hover:border-[#e0507a]/60 hover:text-[#e0507a]"
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                {label}
              </button>
            );
          })}
        </nav>

        <div className="flex flex-col gap-3">
          {visibleItems.map(({ id, category, question, answer }) => {
            const isOpen = openId === id;
            const Icon = categoryIcon[category];
            return (
              <div
                key={id}
                className="relative flex overflow-hidden rounded-2xl bg-white shadow-sm"
              >
                {/* stub - echoes a boarding-pass ticket, category icon marks which pocket this question flies out of */}
                <div className="hidden w-14 shrink-0 items-start justify-center border-r border-dashed border-[#e0507a]/25 pt-5 sm:flex">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#fdf0f4]">
                    <Icon className="h-4 w-4 text-[#e0507a]" />
                  </span>
                </div>
                <span
                  aria-hidden
                  className="pointer-events-none absolute left-14 top-0 hidden h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#fdf0f4] sm:block"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute left-14 bottom-0 hidden h-4 w-4 -translate-x-1/2 translate-y-1/2 rounded-full bg-[#fdf0f4] sm:block"
                />

                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${id}`}
                    onClick={() => setOpenId(isOpen ? null : id)}
                  >
                    <span className="font-[family-name:var(--font-playfair)] text-base leading-snug text-zinc-900 lg:text-lg">
                      {question}
                    </span>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-[#e0507a]/50 text-[#e0507a]">
                      <Plus
                        className={`h-3.5 w-3.5 transition-transform duration-300 motion-reduce:transition-none ${
                          isOpen ? "rotate-45" : ""
                        }`}
                      />
                    </span>
                  </button>
                  <div
                    id={`faq-answer-${id}`}
                    className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="min-h-0">
                      <p className="border-t border-dashed border-[#e0507a]/15 px-6 pb-5 pt-4 text-sm leading-relaxed text-zinc-600">
                        {answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
